import React, { useState, useEffect } from "react";
import axios from "axios";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { PDFDownloadLink } from "@react-pdf/renderer";
import "bootstrap/dist/css/bootstrap.min.css";
import { experienceData, skills } from "../constants/resume";
import {
  experienceData as frontendExperienceData,
  skills as frontendSkills,
} from "../constants/frontendResume";
import {
  experienceData as backendExperienceData,
  skills as backendSkills,
} from "../constants/backendResume";
import { getTotalExperience } from "../services/utilService";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12 },
  section: { marginBottom: 10 },
  text: { fontFamily: "Helvetica", lineHeight: 1.5, whiteSpace: "pre-wrap" },
});

const CoverLetterPDF = ({ coverLetter }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.text}>{coverLetter}</Text>
      </View>
    </Page>
  </Document>
);

const CoverLetterGenerator = () => {
  const [role, setRole] = useState("full-stack");
  const [resumeData, setResumeData] = useState(
    JSON.stringify({ experienceData, skills }, null, 2)
  );
  const [companyName, setCompanyName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [hiringManagerName, setHiringManagerName] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Update resumeData when role changes
  useEffect(() => {
    const resumeMap = {
      frontend: {
        experienceData: frontendExperienceData,
        skills: frontendSkills,
      },
      backend: {
        experienceData: backendExperienceData,
        skills: backendSkills,
      },
      "full-stack": {
        experienceData,
        skills,
      },
    };
    setResumeData(JSON.stringify(resumeMap[role], null, 2));
    setError("");
  }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setCoverLetter("");

    let parsedResumeData;
    try {
      parsedResumeData = JSON.parse(resumeData);
      if (
        !parsedResumeData.experienceData ||
        !Array.isArray(parsedResumeData.experienceData)
      ) {
        throw new Error('Resume data must include an "experienceData" array');
      }
    } catch (err) {
      setError(
        'Invalid resume data: Must be valid JSON with an "experienceData" array'
      );
      setLoading(false);
      return;
    }

    if (!companyName || !jobDescription) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        "https://cover-letter-generator-taupe.vercel.app/generate-cover-letter",
        {
          role,
          resumeData: parsedResumeData,
          companyName,
          jobDescription,
          hiringManagerName,
          experience: getTotalExperience(0.5),
          password,
        }
      );
      setCoverLetter(response.data?.content);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to generate cover letter. Please check the server."
      );
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCoverLetterChange = (e) => {
    setCoverLetter(e.target.value);
  };

  return (
    <div className="container mt-5">
      <h1 className="mb-4 text-center text-white">Cover Letter Generator</h1>
      <div className="row">
        {/* Input Form */}
        <div className="col-md-6 px-3">
          <form onSubmit={handleSubmit} className="mb-4">
            <div className="mb-3">
              <label htmlFor="role" className="form-label text-white">
                Role <span className="text-danger">*</span>
              </label>
              <select
                id="role"
                className="form-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                required
              >
                <option value="frontend">Frontend Developer</option>
                <option value="backend">Backend Developer</option>
                <option value="full-stack">Full-Stack Developer</option>
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="companyName" className="form-label text-white">
                Company Name <span className="text-danger">*</span>
              </label>
              <input
                id="companyName"
                type="text"
                className="form-control"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Stripe"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="jobDescription" className="form-label text-white">
                Job Description <span className="text-danger">*</span>
              </label>
              <textarea
                id="jobDescription"
                className="form-control"
                rows="5"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="e.g., Stripe is seeking a Full-Stack Developer to build scalable payment processing solutions..."
                required
              />
            </div>
            <div className="mb-3">
              <label
                htmlFor="hiringManagerName"
                className="form-label text-white"
              >
                Hiring Manager Name (Optional)
              </label>
              <input
                id="hiringManagerName"
                type="text"
                className="form-control"
                value={hiringManagerName}
                onChange={(e) => setHiringManagerName(e.target.value)}
                placeholder="e.g., Jane Doe"
              />
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="form-label text-white">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Generating...
                </>
              ) : (
                "Generate Cover Letter"
              )}
            </button>
          </form>
        </div>

        {/* Output Section */}
        <div className="col-md-6 px-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="form-label text-white">
              Generated Cover Letter
            </label>
            <textarea
              className="form-control mb-3"
              rows="15"
              value={coverLetter}
              onChange={handleCoverLetterChange}
              placeholder="Your cover letter will appear here..."
            />
            <PDFDownloadLink
              document={<CoverLetterPDF coverLetter={coverLetter} />}
              fileName={`Usama_Masood_${role.replace(
                "-",
                "_"
              )}_Cover_Letter.pdf`}
              className="btn btn-success w-100"
            >
              {({ loading }) =>
                loading ? "Preparing PDF..." : "Download Cover Letter as PDF"
              }
            </PDFDownloadLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverLetterGenerator;
